import { useEffect, useRef, useState } from 'react';

// Animate both dots and labels from the currently drawn position. Timestamp
// deltas determine the duration; identical/stale snapshots never restart motion.
export default function useAnimatedPositions(positions) {
  const drawn = useRef({});
  const targets = useRef({});
  const transitions = useRef({});
  const [visible, setVisible] = useState({});
  useEffect(() => {
    const started = performance.now();
    const moves = transitions.current;
    for (const [key, target] of Object.entries(positions || {})) {
      const previous = targets.current[key];
      if (previous?.date === target.date && previous?.x === target.x && previous?.y === target.y) continue;
      const elapsed = Date.parse(target.date) - Date.parse(previous?.date);
      moves[key] = { from: drawn.current[key] || target, to: target,
        started,
        duration: Number.isFinite(elapsed) ? Math.min(1800, Math.max(200, elapsed)) : 500 };
    }
    targets.current = positions || {};
    for (const key of Object.keys(moves)) if (!positions?.[key]) delete moves[key];
    let frame;
    const animate = time => {
      const next = {};
      let active = false;
      for (const [key, target] of Object.entries(positions || {})) {
        const move = moves[key];
        const progress = move ? Math.min(1, (time - move.started) / move.duration) : 1;
        active ||= progress < 1;
        next[key] = move ? { ...target,
          x: move.from.x + (target.x - move.from.x) * progress,
          y: move.from.y + (target.y - move.from.y) * progress } : target;
      }
      drawn.current = next;
      setVisible(next);
      if (active) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [positions]);
  return visible;
}
